import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { SeasonsService } from '../../services/seasons.service';
import * as SeasonActions from '../actions/season.action';
import { NotificationsService } from '@shared/services/notifications.service';
import { BadResponseWithError } from '@shared/models/common.interface';
import { PanelDialogService } from '@shared/services/panel-dialog.service';

@Injectable({ providedIn: 'root' })
export class SeasonsEffect {
    loadSeasons$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SeasonActions.fetchSeasonsRequest),
            switchMap(() =>
                this.seasonsService.getAllSeasons().pipe(
                    map((seasons) =>
                        SeasonActions.fetchSeasonsSuccess({ payload: seasons })
                    ),
                    catchError((error) =>
                        of(
                            SeasonActions.fetchSeasonsFailure({
                                payload: error,
                            })
                        )
                    )
                )
            )
        )
    );

    loadSeason$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SeasonActions.fetchSeasonRequest),
            switchMap((payload) =>
                this.seasonsService.getSeason(payload.seasonId).pipe(
                    map((season) =>
                        SeasonActions.fetchSeasonSuccess({ payload: season })
                    ),
                    catchError((error) =>
                        of(SeasonActions.fetchSeasonFailure({ payload: error }))
                    )
                )
            )
        )
    );

    createSeason$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SeasonActions.createSeasonRequest),
            switchMap((payload) =>
                this.seasonsService
                    .createSeason(payload.seasonId, payload.body)
                    .pipe(
                        map((season) => {
                            this.notifications.showSuccessMessage(
                                'Season created successfully'
                            );
                            this.panelDialogService.currentDialogRef?.close();
                            return SeasonActions.createSeasonSuccess({
                                payload: season,
                            });
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notifications.showError(
                                response.error.message
                            );
                            return of(
                                SeasonActions.createSeasonFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    updateSeason$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SeasonActions.updateSeasonRequest),
            switchMap((payload) =>
                this.seasonsService
                    .updateSeason(payload.seasonId, payload.body)
                    .pipe(
                        map((season) => {
                            this.notifications.showSuccessMessage(
                                'Season updated successfully'
                            );
                            this.panelDialogService.currentDialogRef?.close();
                            return SeasonActions.updateSeasonSuccess({
                                payload: season,
                            });
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notifications.showError(
                                response.error.message
                            );
                            return of(
                                SeasonActions.updateSeasonFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    deleteSeason$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SeasonActions.deleteSeasonRequest),
            switchMap((payload) =>
                this.seasonsService.deleteSeason(payload.seasonId).pipe(
                    map(() => {
                        this.notifications.showSuccessMessage(
                            'Season deleted successfully'
                        );
                        return SeasonActions.deleteSeasonSuccess({
                            seasonId: payload.seasonId,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notifications.showError(response.error.message);
                        return of(
                            SeasonActions.deleteSeasonFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    saveLevels$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SeasonActions.saveLevelsRequest),
            switchMap((payload) =>
                this.seasonsService
                    .saveLevels(payload.seasonId, payload.body)
                    .pipe(
                        map((levels) => {
                            this.notifications.showSuccessMessage(
                                'Levels saved successfully'
                            );
                            return SeasonActions.saveLevelsSuccess({
                                payload: levels,
                            });
                        }),
                        catchError((error) =>
                            of(
                                SeasonActions.saveLevelsFailure({
                                    payload: error,
                                })
                            )
                        )
                    )
            )
        )
    );

    fetchLevels$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SeasonActions.fetchLevelsRequest),
            switchMap((payload) =>
                this.seasonsService.getLevels(payload.seasonId).pipe(
                    map((levels) =>
                        SeasonActions.fetchLevelsSuccess({ payload: levels })
                    ),
                    catchError((error) =>
                        of(SeasonActions.fetchLevelsFailure({ payload: error }))
                    )
                )
            )
        )
    );

    saveRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SeasonActions.saveRewardsRequest),
            switchMap((payload) =>
                this.seasonsService
                    .saveRewards(payload.seasonId, payload.body)
                    .pipe(
                        map((rewards) => {
                            this.notifications.showSuccessMessage(
                                'Rewards saved successfully'
                            );
                            return SeasonActions.saveRewardsSuccess({
                                payload: rewards,
                            });
                        }),
                        catchError((error) =>
                            of(
                                SeasonActions.saveRewardsFailure({
                                    payload: error,
                                })
                            )
                        )
                    )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private seasonsService: SeasonsService,
        private notifications: NotificationsService,
        private panelDialogService: PanelDialogService
    ) {}
}
